import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import CopyIcon from "@mui/icons-material/CopyAll";
import { t } from "../../i18n/translates";

const convertToTSV = (data: string[][]) => {
    if (!data || data.length === 0) return "";
    const rows = data.map((row) => row.join("\t"));
    return rows.join("\n");
};

const convertToCSV = (data: string[][]) => {
    if (!data || data.length === 0) return "";
    const rows = data.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","));
    return rows.join("\n");
};

export const ExportModal = ({
    onClose,
    tableData,
    fileName,
}: {
    onClose: () => void;
    tableData?: string[][];
    fileName?: string;
}) => {
    if (!tableData) return null;

    const tsvData = convertToTSV(tableData);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(tsvData);
        } catch (err) {
            console.error("Failed to copy:", err);
        }
    };

    const handleDownloadCSV = () => {
        const csv = convertToCSV(tableData);
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${fileName ?? "data"}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t("dashboard.export_data")}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={(theme) => ({
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500],
                })}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent sx={{ p: 1 }}>
                <TextField
                    multiline
                    fullWidth
                    maxRows={8}
                    value={tsvData}
                    variant="outlined"
                    slotProps={{
                        input: {
                            readOnly: true,
                            style: { fontFamily: "monospace", fontSize: "10px" },
                        },
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCopy} color="primary" variant="contained" startIcon={<CopyIcon />}>
                    {t("dashboard.copy_tsv")}
                </Button>
                <Button onClick={handleDownloadCSV} color="secondary" variant="contained" startIcon={<DownloadIcon />}>
                    {t("dashboard.export_csv")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
